import React, { useEffect, useMemo, useRef } from "react";
import cx from "classnames";
import { useField } from "formik";

import InputField from "./InputField";

const normaliseOption = (option) =>
  typeof option === "string" ? { label: option, value: option } : option;

const Dropdown = ({
  className,
  containerClass,
  hint,
  label,
  options,
  placeholder,
  required,
  ...props
}) => {
  const [field, meta] = useField(props);
  const selectRef = useRef(null);
  const isInvalid = Boolean(meta && meta.touched && meta.error);

  const normalisedOptions = useMemo(
    () => (options || []).map(normaliseOption),
    [options],
  );

  const value = field.value == null ? "" : String(field.value);
  const hasMatchingOption = normalisedOptions.some(
    (option) => String(option.value) === value,
  );

  /**
   * A native <select> whose value matches no option falls back to displaying
   * the first one — so the customer sees a country or province they never
   * chose, and submits nothing. Render an explicit empty option so the control
   * can show "nothing selected" honestly.
   */
  const showEmptyOption = Boolean(placeholder) || !hasMatchingOption;

  /**
   * `availableCountries` is seeded from the bundled countries.json and then
   * swapped for the shop's much shorter list once /shop/countries resolves.
   * Removing the selected <option> makes the browser reset selectedIndex to 0,
   * and React won't re-apply `value` because the select's own props didn't
   * change — only its children did. Re-sync explicitly.
   */
  useEffect(() => {
    const select = selectRef.current;

    if (select && select.value !== value) {
      select.value = value;
    }
  }, [normalisedOptions, value]);

  return (
    <InputField
      className={containerClass}
      hint={hint}
      id={props.id}
      label={label}
      meta={meta}
      name={props.name}
      required={required}
    >
      <select
        aria-invalid={isInvalid}
        aria-required={required || undefined}
        className={cx("gkit-select-md", className, isInvalid && "gkit-invalid")}
        ref={selectRef}
        {...field}
        {...props}
      >
        {showEmptyOption && (
          <option value="">{placeholder || "Please select"}</option>
        )}
        {normalisedOptions.map((option) => (
          // Keyed by value, not index: with index keys React rewrites the
          // option list in place when it's swapped, which is what knocks the
          // selection out.
          <option key={String(option.value)} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </InputField>
  );
};

export default Dropdown;
